import { makeObservable, observable, action, runInAction } from 'mobx';
import { getSystemHealth, type HealthStatus, type HealthSummary } from '@/api/systemHealth';
import type { RootStore } from './RootStore';

// The ONE poller of /api/SystemHealth/Live. The System Health page and the
// right rail's System health dot both subscribe here, so an open page never
// runs two timers against the same endpoint.
//
// Each subscriber asks for an interval; the shortest active one wins (the page
// wants 30 s, the rail is content with 60 s). Polling pauses while the tab is
// hidden and catches up the moment it is visible again.

const STALE_AFTER_MISSED_POLLS = 3;

export class SystemHealthStore {
  @observable.ref summary: HealthSummary | null = null;
  @observable error: string | null = null;
  @observable loading = false;
  @observable checkedAt: number | null = null;

  private intervals: number[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private activeInterval = 0;

  constructor(_root: RootStore) {
    void _root;
    makeObservable(this);
    document.addEventListener('visibilitychange', () => this.reschedule());
  }

  /**
   * What the rail dot should say. Never "ok" on a guess: a failed call, no
   * answer yet, or an answer older than three missed polls all read "unknown".
   * A plain getter, not @computed: it reads the clock, which MobX cannot observe.
   */
  get status(): HealthStatus {
    if (this.error || !this.summary || this.checkedAt == null) return 'unknown';
    const pollMs = this.activeInterval || 60_000;
    if (Date.now() - this.checkedAt > pollMs * STALE_AFTER_MISSED_POLLS) return 'unknown';
    return this.summary.overall;
  }

  /** Start (or join) polling. Returns the unsubscribe function. */
  subscribe(intervalMs: number): () => void {
    this.intervals.push(intervalMs);
    if (this.checkedAt == null || Date.now() - this.checkedAt > intervalMs) void this.refresh();
    this.reschedule();
    return () => {
      const i = this.intervals.indexOf(intervalMs);
      if (i !== -1) this.intervals.splice(i, 1);
      this.reschedule();
    };
  }

  @action
  async refresh(): Promise<void> {
    this.loading = true;
    try {
      const s = await getSystemHealth();
      runInAction(() => {
        this.summary = s;
        this.error = null;
        this.checkedAt = Date.now();
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load system health';
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  private reschedule(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.activeInterval = this.intervals.length ? Math.min(...this.intervals) : 0;
    if (!this.activeInterval || document.visibilityState === 'hidden') return;
    if (this.checkedAt != null && Date.now() - this.checkedAt > this.activeInterval) void this.refresh();
    this.timer = setInterval(() => void this.refresh(), this.activeInterval);
  }
}
